import { useEffect, useMemo, useRef, useState } from 'react';
import { useMapContext } from '../../map/MapContext';
import { useWorkspaces } from '../../api/WorkspacesContext';
import { useNavigation } from '../../navigation/NavigationContext';
import { styleForWorkspace } from '../../pages/HomePage/workspaceStyle';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons/Icons';
import { LogoMark } from '../icons/LogoMark';
import { Tooltip } from '../Tooltip/Tooltip';
import { addMonths, formatMonthLabel, monthKeyOf, monthKeyToString, sameMonth, type MonthKey } from './monthUtils';
import './top-bar.css';

const WINDOW = 7; // months shown in the strip

const GRANULARITIES = ['Day', 'Week', 'Month', 'Year'] as const;
type Granularity = (typeof GRANULARITIES)[number];
// Only Month has an actual timeline behind it right now — all our test
// data is a handful of dates within one month, never mind day/week/year
// buckets. Day/Week/Year are real, clickable menu items (not silently
// broken) but stay disabled with an honest reason in their hover card
// rather than pretending to switch to a granularity with no data model
// built for it yet.
const GRANULARITY_ENABLED: Record<Granularity, boolean> = { Day: false, Week: false, Month: true, Year: false };

/**
 * Top bar — layout/structure/spacing modeled on a reference UI/UX pattern
 * the user shared (not this app's own invention, and not that reference's
 * branding: own logo mark, own product names). Two clusters: brand/
 * breadcrumb (left), month navigation (center, absorbing the previous
 * Timeline.tsx date-selection bar). The reference's right-hand cluster
 * (collaborator avatars, Share, Presentations) was all stub UI with no real
 * function and was removed rather than kept as dead decoration — nothing
 * lives there now. Compare and the 2D/3D toggle live in the bottom toolbar,
 * not here — see BottomToolbar.tsx.
 */
export function TopBar() {
  const { dates, defaultDate, activeDate, setActiveDate, compareMode } = useMapContext();
  const { goHome, goToMap, focusGroupId } = useNavigation();
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const currentWorkspace = workspaces.find((w) => w.slug === focusGroupId);

  // ---- workspace switcher (breadcrumb dropdown) ----
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!workspaceOpen) return;
    function onDocPointerDown(e: PointerEvent) {
      if (workspaceRef.current && !workspaceRef.current.contains(e.target as Node)) setWorkspaceOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setWorkspaceOpen(false);
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [workspaceOpen]);

  const dataMonth = useMemo<MonthKey>(
    () => (defaultDate ? monthKeyOf(defaultDate) : monthKeyOf(new Date().toISOString().slice(0, 10))),
    [defaultDate],
  );
  const [centerOffset, setCenterOffset] = useState(0);
  const center = addMonths(dataMonth, centerOffset);
  const half = Math.floor(WINDOW / 2);
  const months = useMemo(() => Array.from({ length: WINDOW }, (_, i) => addMonths(center, i - half)), [center, half]);

  const activeMonth = activeDate ? monthKeyOf(activeDate) : dataMonth;

  function datesInMonth(m: MonthKey): string[] {
    return dates.filter((d) => sameMonth(monthKeyOf(d), m));
  }

  // ---- "Month" granularity dropdown (Day/Week/Month/Year) ----
  const [granularity, setGranularity] = useState<Granularity>('Month');
  const [granularityOpen, setGranularityOpen] = useState(false);
  const granularityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!granularityOpen) return;
    function onDocPointerDown(e: PointerEvent) {
      if (granularityRef.current && !granularityRef.current.contains(e.target as Node)) setGranularityOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setGranularityOpen(false);
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [granularityOpen]);

  return (
    <header className="top-bar">
      <div className="top-bar-cluster top-bar-left">
        <Tooltip heading="Home" description="Back to your workspaces.">
          <button type="button" className="top-bar-logo" onClick={goHome} aria-label="Go to home">
            <LogoMark size={26} />
          </button>
        </Tooltip>
        <div className="top-bar-workspace-dropdown" ref={workspaceRef}>
          <Tooltip heading="Switch workspace" description="Jump to another workspace.">
            <button
              type="button"
              className="top-bar-breadcrumb"
              aria-haspopup="menu"
              aria-expanded={workspaceOpen}
              onClick={() => setWorkspaceOpen((v) => !v)}
            >
              <span className="top-bar-breadcrumb-project">Abrar Farms</span>
              <span className="top-bar-breadcrumb-sep">/</span>
              <span className="top-bar-breadcrumb-context">{currentWorkspace?.title ?? 'Maps'}</span>
              <ChevronDownIcon />
            </button>
          </Tooltip>
          {workspaceOpen && (
            <div className="top-bar-workspace-menu" role="menu" aria-label="Workspaces">
              {workspacesState.status === 'loading' && <div className="top-bar-workspace-note">Loading workspaces…</div>}
              {workspacesState.status === 'error' && (
                <div className="top-bar-workspace-note">
                  Couldn't load workspaces.{' '}
                  <button type="button" className="top-bar-workspace-retry" onClick={workspacesState.retry}>
                    Retry
                  </button>
                </div>
              )}
              {workspaces.map((w) => {
                const { Icon, accent } = styleForWorkspace(w.icon);
                const isCurrent = w.slug === focusGroupId;
                return (
                  <button
                    key={w.workspace_id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    className={`top-bar-workspace-item${isCurrent ? ' active' : ''}`}
                    onClick={() => {
                      setWorkspaceOpen(false);
                      if (!isCurrent) goToMap(w.slug);
                    }}
                  >
                    <span className="top-bar-workspace-item-icon" style={{ color: accent }}>
                      <Icon size={18} />
                    </span>
                    <span className="top-bar-workspace-item-title">{w.title}</span>
                  </button>
                );
              })}
              <div className="top-bar-workspace-sep" />
              <button
                type="button"
                role="menuitem"
                className="top-bar-workspace-item"
                onClick={() => {
                  setWorkspaceOpen(false);
                  goHome();
                }}
              >
                <span className="top-bar-workspace-item-title">All workspaces</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="top-bar-cluster top-bar-center">
        <div className="top-bar-month-dropdown" ref={granularityRef}>
          <Tooltip heading="View range" description="Choose the timeline's granularity.">
            <button
              type="button"
              className="top-bar-month-label"
              aria-haspopup="menu"
              aria-expanded={granularityOpen}
              onClick={() => setGranularityOpen((v) => !v)}
            >
              {granularity} <ChevronDownIcon />
            </button>
          </Tooltip>
          {granularityOpen && (
            <div className="top-bar-granularity-menu" role="menu" aria-label="Timeline granularity">
              {GRANULARITIES.map((g) => {
                const enabled = GRANULARITY_ENABLED[g];
                const isSelected = granularity === g;
                return (
                  <Tooltip
                    key={g}
                    heading={g}
                    description={enabled ? 'Currently showing the timeline month by month.' : `No ${g.toLowerCase()}-level data yet.`}
                  >
                    {/* A plain <span> wrapper, not a native `disabled` button, for the
                        unavailable options — a disabled button doesn't reliably fire
                        the hover events Tooltip needs in Chrome (see
                        ViewToggle2D3D.tsx's identical fix), which would silently make
                        these items look inert with no explanation at all. The click is
                        still guarded below so selecting one is a real no-op. */}
                    <span className={`top-bar-granularity-item-wrap${enabled ? '' : ' disabled'}`}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={isSelected}
                        aria-disabled={!enabled}
                        className={`top-bar-granularity-item${isSelected ? ' active' : ''}${enabled ? '' : ' disabled'}`}
                        onClick={() => {
                          if (!enabled) return;
                          setGranularity(g);
                          setGranularityOpen(false);
                        }}
                      >
                        {g}
                      </button>
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
        <div className="top-bar-nav-pill">
          <button type="button" aria-label="Show earlier months" onClick={() => setCenterOffset((o) => o - 1)}>
            <ChevronLeftIcon />
          </button>
          <button type="button" aria-label="Show later months" onClick={() => setCenterOffset((o) => o + 1)}>
            <ChevronRightIcon />
          </button>
        </div>
        <div className="top-bar-months" role="tablist" aria-label="Month">
          {months.map((m) => {
            const available = datesInMonth(m);
            const isActive = sameMonth(m, activeMonth);
            const hasData = available.length > 0;
            // Real reason a tab can't be selected right now, shown via the
            // hover card below — distinct messages so "there's no data"
            // and "you're mid-compare" don't look like the same failure.
            const disabledReason = compareMode
              ? 'Exit Compare to pick a single month.'
              : !hasData
                ? 'No data for this month yet.'
                : null;
            const selectable = !disabledReason;
            return (
              <Tooltip
                key={monthKeyToString(m)}
                heading={formatMonthLabel(m)}
                description={disabledReason ?? `${available.length} dated layer(s) available.`}
              >
                {/* Same reasoning as the granularity items above: a plain
                    <span> + guarded onClick, not a native `disabled`
                    button, so every month tab (including ones with no
                    data) reliably shows its hover explanation instead of
                    going silently inert — this was the actual bug behind
                    "clicking a month tab doesn't select it": disabled
                    buttons don't fire the mouse events Tooltip listens
                    for in Chrome, so those tabs gave no feedback at all
                    and looked broken rather than merely unavailable.
                    Months that DO have data were already clickable
                    before this fix — confirmed live, this only fixes the
                    disabled ones' missing feedback and tightens the
                    guard to match. */}
                <span className={`top-bar-month-tab-wrap${selectable ? '' : ' disabled'}`}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-disabled={!selectable}
                    className={`top-bar-month-tab${isActive ? ' active' : ''}${!hasData ? ' empty' : ''}`}
                    onClick={() => {
                      if (!selectable) return;
                      setActiveDate(available[available.length - 1]);
                    }}
                  >
                    {formatMonthLabel(m)}
                  </button>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </header>
  );
}
